import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { Category, CategoryWithProduct } from '../interfaces/category.interface';
import { Product } from '../interfaces/product.interface';
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  query, 
  where, 
  addDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase.config';
import { map, catchError, shareReplay, startWith } from 'rxjs/operators';
import { ProductService } from './product.service';

@Injectable({
  providedIn: 'root'
})
export class CategoryService {
  private categoriesSubject = new BehaviorSubject<Category[]>([]);
  public categories$ = this.categoriesSubject.asObservable().pipe(shareReplay(1));

  private categoriesLoadingSubject = new BehaviorSubject<boolean>(true);
  public categoriesLoading$ = this.categoriesLoadingSubject.asObservable();

  // Cache management
  private categoriesCache: Category[] | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache
  private isLoadingCategories = false;

  constructor(private productService: ProductService) {
    // Check cache first and set loading state accordingly
    const now = Date.now();
    if (this.categoriesCache && (now - this.cacheTimestamp) < this.CACHE_DURATION) {
      this.categoriesSubject.next(this.categoriesCache);
      this.categoriesLoadingSubject.next(false);
    } else {
      this.loadCategoriesFromDatabase();
    }
  }

  /**
   * Load all categories from Firebase
   */
  private async loadCategoriesFromDatabase(): Promise<void> {
    if (this.isLoadingCategories) {
      return;
    }

    const now = Date.now();
    if (this.categoriesCache && (now - this.cacheTimestamp) < this.CACHE_DURATION) {
      return;
    }

    try {
      this.isLoadingCategories = true;
      this.categoriesLoadingSubject.next(true);
      
      const categoriesRef = collection(db, 'categories');
      const querySnapshot = await getDocs(categoriesRef);
      
      const categories: Category[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        // Debug: Log raw data from Firebase
        console.log(`Loading category ${doc.id} from Firebase:`, {
          rawData: data,
          productIdsRaw: data['productIds'],
          productIdsType: typeof data['productIds'],
          productIdsIsArray: Array.isArray(data['productIds']),
          productIdsLength: Array.isArray(data['productIds']) ? data['productIds'].length : 'N/A',
          subProductsRaw: data['subProducts'],
          subProductsType: typeof data['subProducts'],
          subProductsIsArray: Array.isArray(data['subProducts']),
          subProductsLength: Array.isArray(data['subProducts']) ? data['subProducts'].length : 'N/A'
        });
        
        // Ensure productIds is always an array
        // Check if productIds exists (even if empty array, it exists)
        let productIds: string[] = [];
        if (data['productIds'] !== undefined && data['productIds'] !== null) {
          if (Array.isArray(data['productIds'])) {
            productIds = data['productIds']; // Use array as-is (even if empty)
          } else if (typeof data['productIds'] === 'string') {
            productIds = [data['productIds']];
          }
        } else if (data['productId'] !== undefined && data['productId'] !== null) {
          // Handle legacy productId (single string)
          productIds = typeof data['productId'] === 'string' ? [data['productId']] : [];
        }
        
        // Ensure subProducts is always an array
        // Check if subProducts exists (even if empty array, it exists)
        let subProducts: string[] = [];
        if (data['subProducts'] !== undefined && data['subProducts'] !== null) {
          if (Array.isArray(data['subProducts'])) {
            subProducts = data['subProducts']; // Use array as-is (even if empty)
          } else if (typeof data['subProducts'] === 'string') {
            subProducts = [data['subProducts']];
          }
        }
        
        console.log(`After processing category ${doc.id}:`, {
          productIds: productIds,
          productIdsLength: productIds.length,
          subProducts: subProducts,
          subProductsLength: subProducts.length
        });
        
        // Build category object explicitly to ensure productIds is set correctly
        const category: Category = {
          id: doc.id,
          name: data['name'] || '',
          price: data['price'] || 0,
          originalPrice: data['originalPrice'],
          productIds: productIds, // Always set as normalized array
          subProducts: subProducts, // Always set as normalized array
          isActive: data['isActive'] !== undefined ? data['isActive'] : true,
          i18n: data['i18n'],
          createdAt: data['createdAt']?.toDate?.() || data['createdAt'],
          updatedAt: data['updatedAt']?.toDate?.() || data['updatedAt']
        };
        
        categories.push(category);
      });
      
      // Sort categories by first productId, then by price (low to high)
      categories.sort((a, b) => {
        const aFirstProductId = (a.productIds && a.productIds.length > 0) ? a.productIds[0] : '';
        const bFirstProductId = (b.productIds && b.productIds.length > 0) ? b.productIds[0] : '';
        if (aFirstProductId !== bFirstProductId) {
          return aFirstProductId.localeCompare(bFirstProductId);
        }
        return (Number(a.price) || 0) - (Number(b.price) || 0);
      });
      
      // Update cache
      this.categoriesCache = categories;
      this.cacheTimestamp = now;
      
      this.categoriesSubject.next(categories);
      this.categoriesLoadingSubject.next(false);
      this.isLoadingCategories = false;
    } catch (error) {
      console.error('Error loading categories from database:', error);
      
      if (this.categoriesCache) {
        this.categoriesSubject.next(this.categoriesCache);
      } else {
        this.categoriesSubject.next([]);
      }
      this.categoriesLoadingSubject.next(false);
      this.isLoadingCategories = false;
    }
  }

  /**
   * Get all categories
   */
  getCategories(): Observable<Category[]> {
    return this.categories$.pipe(
      startWith([]),
      catchError(error => {
        console.error('Error getting categories:', error);
        return of([]);
      }),
      shareReplay(1)
    );
  }

  /**
   * Get categories by product ID
   */
  getCategoriesByProductId(productId: string): Observable<Category[]> {
    return from(this.getCategoriesByProductIdFromDb(productId)).pipe(
      catchError(error => {
        console.error('Error fetching categories by product ID:', error);
        const currentCategories = this.categoriesSubject.value;
        return of(currentCategories.filter(c => {
          const productIds = c.productIds || ((c as any).productId ? [(c as any).productId] : []);
          return productIds.includes(productId);
        }));
      })
    );
  }

  /**
   * Get categories by product ID from database
   * Note: Firestore doesn't support array-contains queries directly on array fields in all cases,
   * so we fetch all categories and filter in memory for now
   */
  private async getCategoriesByProductIdFromDb(productId: string): Promise<Category[]> {
    try {
      // Fetch all categories and filter by productIds array
      const categories = await this.getAllCategoriesFromDb();
      return categories
        .filter(c => {
          const productIds = c.productIds || ((c as any).productId ? [(c as any).productId] : []);
          return productIds.includes(productId);
        })
        .sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0));
    } catch (error) {
      console.error('Error fetching categories by product ID from database:', error);
      throw error;
    }
  }

  /**
   * Get category by ID
   */
  getCategoryById(id: string): Observable<Category | undefined> {
    return from(this.getCategoryByIdFromDb(id)).pipe(
      catchError(error => {
        console.error('Error fetching category by ID:', error);
        const currentCategories = this.categoriesSubject.value;
        return of(currentCategories.find(c => c.id === id));
      })
    );
  }

  /**
   * Get category by ID from database
   */
  private async getCategoryByIdFromDb(id: string): Promise<Category | undefined> {
    try {
      const docRef = doc(db, 'categories', id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        
        // Ensure productIds is always an array
        let productIds: string[] = [];
        if (data['productIds']) {
          productIds = Array.isArray(data['productIds']) 
            ? data['productIds'] 
            : (typeof data['productIds'] === 'string' ? [data['productIds']] : []);
        } else if (data['productId']) {
          // Handle legacy productId (single string)
          productIds = typeof data['productId'] === 'string' ? [data['productId']] : [];
        }
        
        // Ensure subProducts is always an array
        let subProducts: string[] = [];
        if (data['subProducts']) {
          subProducts = Array.isArray(data['subProducts']) 
            ? data['subProducts'] 
            : (typeof data['subProducts'] === 'string' ? [data['subProducts']] : []);
        }
        
        return {
          id: docSnap.id,
          ...data,
          productIds: productIds,
          subProducts: subProducts,
          createdAt: data['createdAt']?.toDate?.() || data['createdAt'],
          updatedAt: data['updatedAt']?.toDate?.() || data['updatedAt']
        } as Category;
      }
      return undefined;
    } catch (error) {
      console.error('Error fetching category by ID from database:', error);
      throw error;
    }
  }

  /**
   * Get categories with product information
   */
  getCategoriesWithProductInfo(): Observable<CategoryWithProduct[]> {
    return this.categories$.pipe(
      map(categories => {
        // This would need product data - we'll enhance this later
        return categories.map(cat => cat as CategoryWithProduct);
      }),
      catchError(error => {
        console.error('Error getting categories with product info:', error);
        return of([]);
      })
    );
  }

  /**
   * Create a new category
   */
  async createCategory(categoryData: Omit<Category, 'id'>): Promise<string> {
    try {
      // Generate sequential ID
      const nextId = await this.getNextSequentialId();
      
      // Ensure productIds and subProducts are arrays before saving
      const dataToSave = {
        ...categoryData,
        productIds: Array.isArray(categoryData.productIds) ? categoryData.productIds : (categoryData.productIds ? [categoryData.productIds] : []),
        subProducts: Array.isArray(categoryData.subProducts) ? categoryData.subProducts : (categoryData.subProducts ? [categoryData.subProducts] : []),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      console.log('Creating category - dataToSave:', {
        productIds: dataToSave.productIds,
        productIdsLength: dataToSave.productIds.length,
        subProducts: dataToSave.subProducts,
        subProductsLength: dataToSave.subProducts.length
      });
      
      // Filter out undefined values (but preserve empty arrays)
      const cleanCategoryData = this.filterUndefinedValues(dataToSave);
      
      console.log('After filterUndefinedValues:', {
        productIds: cleanCategoryData.productIds,
        productIdsLength: cleanCategoryData.productIds?.length,
        subProducts: cleanCategoryData.subProducts,
        subProductsLength: cleanCategoryData.subProducts?.length
      });
      
      // Remove id from data before saving (Firestore will use the document ID)
      const { id, ...dataWithoutId } = cleanCategoryData as any;
      
      console.log('Saving to Firebase:', {
        documentId: nextId,
        productIds: dataWithoutId.productIds,
        subProducts: dataWithoutId.subProducts
      });
      
      await setDoc(doc(db, 'categories', nextId), dataWithoutId);
      
      // Refresh categories list
      await this.loadCategoriesFromDatabase();
      
      return nextId;
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  }

  /**
   * Update an existing category
   */
  async updateCategory(categoryId: string, categoryData: Partial<Category>): Promise<void> {
    try {
      // Ensure productIds and subProducts are arrays before saving
      const dataToSave: any = {
        ...categoryData,
        updatedAt: Timestamp.now()
      };
      
      if (categoryData.productIds !== undefined) {
        dataToSave.productIds = Array.isArray(categoryData.productIds) 
          ? categoryData.productIds 
          : (categoryData.productIds ? [categoryData.productIds] : []);
      }
      
      if (categoryData.subProducts !== undefined) {
        dataToSave.subProducts = Array.isArray(categoryData.subProducts) 
          ? categoryData.subProducts 
          : (categoryData.subProducts ? [categoryData.subProducts] : []);
      }
      
      const cleanCategoryData = this.filterUndefinedValues(dataToSave);
      
      const docRef = doc(db, 'categories', categoryId);
      await updateDoc(docRef, cleanCategoryData);
      console.log('Category updated successfully');
      
      // Refresh categories list
      await this.loadCategoriesFromDatabase();
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  }

  /**
   * Add a product to a category's productIds (keeps product–category in sync for product detail).
   */
  async addProductToCategory(categoryId: string, productId: string): Promise<void> {
    const category = await this.getCategoryByIdFromDb(categoryId);
    if (!category) return;
    const productIds = category.productIds || [];
    if (productIds.includes(productId)) return;
    await this.updateCategory(categoryId, { productIds: [...productIds, productId] });
  }

  /**
   * Remove a product from a category's productIds.
   */
  async removeProductFromCategory(categoryId: string, productId: string): Promise<void> {
    const category = await this.getCategoryByIdFromDb(categoryId);
    if (!category) return;
    const productIds = (category.productIds || []).filter(id => id !== productId);
    await this.updateCategory(categoryId, { productIds });
  }

  /**
   * Delete a category
   */
  async deleteCategory(categoryId: string): Promise<void> {
    try {
      const docRef = doc(db, 'categories', categoryId);
      await deleteDoc(docRef);
      console.log('Category deleted successfully');
      
      // Refresh categories list
      await this.loadCategoriesFromDatabase();
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  }

  /**
   * Toggle category active status
   */
  async toggleCategoryStatus(categoryId: string, isActive: boolean): Promise<void> {
    try {
      const docRef = doc(db, 'categories', categoryId);
      await updateDoc(docRef, { 
        isActive,
        updatedAt: Timestamp.now()
      });
      console.log('Category status toggled successfully');
      
      // Refresh categories list
      await this.loadCategoriesFromDatabase();
    } catch (error) {
      console.error('Error toggling category status:', error);
      throw error;
    }
  }

  /**
   * Get the next sequential ID for categories
   */
  private async getNextSequentialId(): Promise<string> {
    try {
      const categories = await this.getAllCategoriesFromDb();
      
      if (categories.length === 0) {
        return '1';
      }
      
      const numericIds = categories
        .map(c => c.id)
        .filter(id => !isNaN(Number(id)))
        .map(id => Number(id));
      
      if (numericIds.length === 0) {
        return '1';
      }
      
      const maxId = Math.max(...numericIds);
      const nextId = (maxId + 1).toString();
      
      console.log(`Next sequential category ID: ${nextId} (max found: ${maxId})`);
      return nextId;
    } catch (error) {
      console.error('Error getting next sequential ID:', error);
      return Date.now().toString();
    }
  }

  /**
   * Get all categories from database (helper method)
   */
  private async getAllCategoriesFromDb(): Promise<Category[]> {
    try {
      const querySnapshot = await getDocs(collection(db, 'categories'));
      const categories: Category[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        // Ensure productIds is always an array
        let productIds: string[] = [];
        if (data['productIds']) {
          productIds = Array.isArray(data['productIds']) 
            ? data['productIds'] 
            : (typeof data['productIds'] === 'string' ? [data['productIds']] : []);
        } else if (data['productId']) {
          // Handle legacy productId (single string)
          productIds = typeof data['productId'] === 'string' ? [data['productId']] : [];
        }
        
        // Ensure subProducts is always an array
        let subProducts: string[] = [];
        if (data['subProducts']) {
          subProducts = Array.isArray(data['subProducts']) 
            ? data['subProducts'] 
            : (typeof data['subProducts'] === 'string' ? [data['subProducts']] : []);
        }
        
        categories.push({
          id: doc.id,
          ...data,
          productIds: productIds,
          subProducts: subProducts,
          createdAt: data['createdAt']?.toDate?.() || data['createdAt'],
          updatedAt: data['updatedAt']?.toDate?.() || data['updatedAt']
        } as Category);
      });
      
      return categories;
    } catch (error) {
      console.error('Error fetching all categories from database:', error);
      return [];
    }
  }

  /**
   * Remove undefined values from an object to prevent Firebase errors
   * IMPORTANT: Preserves empty arrays (they are valid data)
   */
  private filterUndefinedValues(obj: any): any {
    if (obj === null || obj === undefined) {
      return {};
    }
    
    const filtered: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        // Preserve arrays (even empty ones) - they are valid data
        if (Array.isArray(value)) {
          filtered[key] = value;
        } else if (typeof value === 'object' && value !== null && !(value instanceof Date) && !(value instanceof Timestamp)) {
          const filteredValue = this.filterUndefinedValues(value);
          if (Object.keys(filteredValue).length > 0) {
            filtered[key] = filteredValue;
          }
        } else {
          filtered[key] = value;
        }
      }
    }
    return filtered;
  }

  /**
   * Parse sub-products string to array
   */
  parseSubProducts(subProductsString: string): string[] {
    if (!subProductsString) return [];
    return subProductsString.split(',').map(item => item.trim()).filter(item => item.length > 0);
  }

  /**
   * Format sub-products array to string
   */
  formatSubProducts(subProductsArray: string[]): string {
    if (!subProductsArray || subProductsArray.length === 0) return '';
    return subProductsArray.join(', ');
  }

  /**
   * Refresh categories from database
   */
  async refreshCategories(): Promise<void> {
    this.categoriesCache = null;
    this.cacheTimestamp = 0;
    await this.loadCategoriesFromDatabase();
  }

  /**
   * Clear cache manually
   */
  clearCache(): void {
    this.categoriesCache = null;
    this.cacheTimestamp = 0;
  }
}

